import React from "react";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import VerticalAlignTopRoundedIcon from "@mui/icons-material/VerticalAlignTopRounded";
import VerticalAlignBottomRoundedIcon from "@mui/icons-material/VerticalAlignBottomRounded";

function id(item) { return String(item?.id || item?._id || ""); }
function label(item) { return String(item?.name || item?.nombre || "Sin nombre"); }
function section(item) { return String(item?.gastronomicMenu?.section || item?.category || item?.categoria || "").trim(); }
function order(item) { const n = Number(item?.gastronomicMenu?.sortOrder); return Number.isFinite(n) ? n : 0; }
function same(a, b) { return String(a || "").trim().toLocaleLowerCase("es") === String(b || "").trim().toLocaleLowerCase("es"); }

export default function GastronomicSectionPositionEditor({ products = [], currentProductId = "", currentName = "Este producto", sectionName = "", sortOrder = 0, onChange, onOpenFullOrder }) {
  const peers = products
    .filter((item) => item?.gastronomicMenu?.enabled === true && same(section(item), sectionName) && id(item) !== String(currentProductId || ""))
    .sort((a, b) => order(a) - order(b) || label(a).localeCompare(label(b), "es"));
  const current = { __current: true, name: currentName || "Este producto", gastronomicMenu: { sortOrder: Number(sortOrder || 0) } };
  const all = [...peers, current].sort((a, b) => order(a) - order(b) || (a.__current ? 1 : b.__current ? -1 : label(a).localeCompare(label(b), "es")));
  const currentIndex = all.findIndex((item) => item.__current);

  const moveTo = (nextIndex) => {
    const without = all.filter((item) => !item.__current);
    const target = Math.max(0, Math.min(without.length, nextIndex));
    const previous = without[target - 1];
    const next = without[target];
    let nextOrder = 0;
    if (!previous && next) nextOrder = order(next) - 10;
    else if (previous && !next) nextOrder = order(previous) + 10;
    else if (previous && next) nextOrder = (order(previous) + order(next)) / 2;
    onChange(String(nextOrder));
  };

  if (!String(sectionName || "").trim()) return null;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
      <Stack spacing={1.2}>
        <Box>
          <Typography sx={{ fontWeight: 900, color: "var(--wp-panel-text-primary, #19242F)" }}>Posición dentro de “{sectionName}”</Typography>
          <Typography variant="caption" color="text.secondary">No necesitás escribir números. Ubicá visualmente este producto; WebProfit sigue guardando el orden compatible existente.</Typography>
        </Box>
        <Stack spacing={0.6} sx={{ maxHeight: 280, overflowY: "auto", pr: 0.5 }}>
          {all.map((item, index) => (
            <Stack key={item.__current ? "current" : id(item)} direction="row" spacing={1} alignItems="center" sx={{ p: 0.8, borderRadius: 2, bgcolor: item.__current ? "rgba(243,90,58,.08)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.025)" }}>
              <Typography variant="caption" sx={{ width: 24, fontWeight: 900 }}>{index + 1}</Typography>
              <Typography variant="body2" sx={{ flex: 1, fontWeight: item.__current ? 900 : 650 }}>{item.__current ? currentName || "Este producto" : label(item)}</Typography>
              {item.__current ? <Chip size="small" label="Este producto" color="warning" /> : null}
            </Stack>
          ))}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Button type="button" size="small" startIcon={<VerticalAlignTopRoundedIcon />} disabled={currentIndex <= 0} onClick={() => moveTo(0)} sx={{ textTransform: "none" }}>Primero</Button>
          <Button type="button" size="small" startIcon={<ArrowUpwardRoundedIcon />} disabled={currentIndex <= 0} onClick={() => moveTo(currentIndex - 1)} sx={{ textTransform: "none" }}>Subir</Button>
          <Button type="button" size="small" startIcon={<ArrowDownwardRoundedIcon />} disabled={currentIndex >= all.length - 1} onClick={() => moveTo(currentIndex + 1)} sx={{ textTransform: "none" }}>Bajar</Button>
          <Button type="button" size="small" startIcon={<VerticalAlignBottomRoundedIcon />} disabled={currentIndex >= all.length - 1} onClick={() => moveTo(withoutLength(peers))} sx={{ textTransform: "none" }}>Último</Button>
          {onOpenFullOrder ? <Button type="button" size="small" variant="outlined" onClick={onOpenFullOrder} sx={{ textTransform: "none", ml: { sm: "auto" } }}>Organizar toda la Carta</Button> : null}
        </Stack>
      </Stack>
    </Paper>
  );
}

function withoutLength(peers) { return Array.isArray(peers) ? peers.length : 0; }
